import { useCallback, useEffect, useRef, useState } from 'react';

import type { Question, QuestionProperties } from '@api/instant_survey/utilities/types';
import { Button, ErrorText, Fieldset, Input, Layout, OverlayPopover, Pill } from '@utilities';

import {
  RESPONSE_LENGTH_DEFAULT_MAX,
  RESPONSE_LENGTH_DEFAULT_MIN,
  RESPONSE_LENGTH_MAX_ALLOWED,
  VALIDATION_MESSAGES,
} from '../../../../utilities/constants';

import styles from './_index.module.scss';

interface ResponseLengthProps {
  question: Question;
  updateQuestionProperties: (properties: Partial<QuestionProperties>) => void;
}

interface ResponseLengthValues {
  maxLength: number;
  minLength: number;
}

const getInitialValues = (properties: QuestionProperties): ResponseLengthValues => ({
  maxLength: properties.maxLength ?? RESPONSE_LENGTH_DEFAULT_MAX,
  minLength: properties.minLength ?? RESPONSE_LENGTH_DEFAULT_MIN,
});

const ResponseLength = ({ question, updateQuestionProperties }: ResponseLengthProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [valuesToApply, setValuesToApply] = useState<ResponseLengthValues>(
    getInitialValues(question.properties)
  );

  const pillRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isPopoverOpen) {
      setValuesToApply(getInitialValues(question.properties));
    }
  }, [isPopoverOpen]);

  const handleApply = useCallback(() => {
    updateQuestionProperties({
      maxLength: valuesToApply.maxLength,
      minLength: valuesToApply.minLength,
    });
    setIsPopoverOpen(false);
  }, [valuesToApply, updateQuestionProperties]);

  const handleChange = useCallback(
    (key: 'maxLength' | 'minLength') => (value: number | string) => {
      const parsed = parseInt(value as string, 10);
      setValuesToApply((prev) => ({ ...prev, [key]: isNaN(parsed) ? undefined : parsed }));
    },
    []
  );

  const minLength = valuesToApply.minLength ?? RESPONSE_LENGTH_DEFAULT_MIN;
  const maxLength = valuesToApply.maxLength ?? RESPONSE_LENGTH_DEFAULT_MAX;

  const getErrorMessage = () => {
    if (valuesToApply.maxLength === undefined || valuesToApply.minLength === undefined) {
      return null;
    }

    if (valuesToApply.minLength < 1) return VALIDATION_MESSAGES.MIN_LENGTH_POSITIVE_ERROR;

    if (valuesToApply.maxLength <= valuesToApply.minLength)
      return VALIDATION_MESSAGES.MAX_LENGTH_MIN_LENGTH_ERROR;

    if (valuesToApply.maxLength > RESPONSE_LENGTH_MAX_ALLOWED)
      return VALIDATION_MESSAGES.MAX_LENGTH_EXCEEDED_ERROR;

    return null;
  };

  const errorMessage = getErrorMessage();

  const savedMinLength = question.properties.minLength ?? RESPONSE_LENGTH_DEFAULT_MIN;
  const savedMaxLength = question.properties.maxLength ?? RESPONSE_LENGTH_DEFAULT_MAX;

  const isApplyDisabled =
    valuesToApply.minLength === undefined ||
    valuesToApply.maxLength === undefined ||
    isNaN(valuesToApply.minLength) ||
    isNaN(valuesToApply.maxLength) ||
    valuesToApply.minLength < RESPONSE_LENGTH_DEFAULT_MIN ||
    valuesToApply.maxLength <= valuesToApply.minLength ||
    valuesToApply.maxLength > RESPONSE_LENGTH_MAX_ALLOWED ||
    (valuesToApply.minLength === savedMinLength && valuesToApply.maxLength === savedMaxLength);

  return (
    <>
      <Layout.Flex className={styles['response-length']} gap="x-small">
        <span ref={popoverRef}>Allow length of response between</span>
        <Pill
          buttonRef={pillRef}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          value={`${savedMinLength} and ${savedMaxLength} characters`}
        />
      </Layout.Flex>
      <OverlayPopover
        className={styles['response-length-popover']}
        container={popoverRef.current!}
        hasArrow
        isActive={isPopoverOpen}
        onHide={() => setIsPopoverOpen(false)}
        target={pillRef.current}
      >
        <>
          <Fieldset legend="Allow length of response between">
            <Layout.Flex className={styles['response-length-row']} gap="small">
              <Input
                id="min-length-input"
                min={RESPONSE_LENGTH_DEFAULT_MIN}
                onChange={handleChange('minLength')}
                type="number"
                value={minLength}
              />
              <Input
                id="max-length-input"
                max={RESPONSE_LENGTH_MAX_ALLOWED}
                min={(valuesToApply.minLength ?? RESPONSE_LENGTH_DEFAULT_MIN) + 1}
                onChange={handleChange('maxLength')}
                type="number"
                value={maxLength}
              />
            </Layout.Flex>
            {errorMessage && (
              <ErrorText className={styles['response-length-error']} text={errorMessage} />
            )}
          </Fieldset>
          <Button isDisabled={isApplyDisabled} onClick={handleApply} text="Apply" />
        </>
      </OverlayPopover>
    </>
  );
};

export default ResponseLength;
